import React, { useState, useEffect } from 'react';

import { List, ListItem } from '@powerednow/components/List/List';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import SettingsIcon from '@mui/icons-material/Settings';
import { complexFindModel, ComplexModelEntity } from '@powerednow/models/modelLookup';
import { DataWrapper, getDataWrapper } from 'FieldServices/singletons/DataWrapper';
import { AuthManager } from 'FieldServices/singletons/AuthManager';
import { useToast } from '@powerednow/components/toast/toast';
import { ListItemButton } from '@mui/material';
import Skeleton from '@mui/material/Skeleton';
import { styled } from '@mui/material/styles';
import Optional from '@powerednow/components/fields/Optional';
import Button from '@powerednow/components/button/Button';
import Dialog from '@powerednow/components/Dialog/Dialog';
import DefaultListItemCard from 'FieldServices/react/features/workflow/ListItemCards/DefaultListItemCard';
import { ListItemCardProps } from 'FieldServices/react/features/workflow/ListItemCards/ListItemCardProps';
import SelectWorkflowPopup from './SelectWorkflowPopup';
import CompanyWorkflowSettings from './companyWorkflowSettings';
import { CompanyWorkflowWithAssociatedWorkflow, useCompanyWorkflow, useWorkflow } from './hooks';

const CompanyWorkflowListRoot = styled('div', {
    name: 'CompanyWorkflowList',
    slot: 'root',
})(() => ({
    display: 'flex',
    width: '100%',
}));

const CompanyWorkflowListItemContainer = styled(List, {
    name: 'CompanyWorkflowList',
    slot: 'listItemContainer',
})(() => ({
    marginTop: '12px',
}));

const CompanyWorkFlowListItem = styled(ListItem, {
    name: 'WorkFlowListItem',
    slot: 'root',
})(({ theme }) => ({
    borderRadius: '5px',
    border: `1px solid ${theme.palette.appSpecificColors?.silverBase}`,
    marginBottom: '5px',
    '& .MuiListItemIcon-root': {
        flexDirection: 'column',
        gap: '20px',
        margin: '5px',
    },
}));

const CompanyWorkflowListItemButton = styled(ListItemButton, {
    name: 'CompanyWorkflowListItem',
    slot: 'root',
})(args => {
    const { theme } = args;
    return {
        '&.Mui-selected': {
            backgroundColor: 'white',
            color: theme.palette.text.primary,
            '&:hover': {
                color: theme.palette.text.primary,
                backgroundColor: 'white',
            },
        },
        '&:hover': {
            backgroundColor: 'white',
            color: theme.palette.text.primary,
        },
    };
});

const CompanyWorkflowListDialog = styled(Dialog, {
    name: 'CompanyWorkflowList',
    slot: 'dialog',
})(({ theme }) => ({
    '.MuiDialogContent-root': {
        [theme.breakpoints.down('sm')]: {
            padding: 0,
        },
    },
}));

const OptionalListItem = Optional(ListItem);
const OptionalCompanyWorkflowContainer = Optional(CompanyWorkflowListItemContainer);

export default function CompanyWorkflowList({
    onItemSelected = () => {},
    showAddButton = true,
    addButtonTitle = 'Add new workflow',
    workflowIdFilter = '',
    ListItemCard = DefaultListItemCard,
    editPanelVisible = true,
}: {
    onItemSelected?: (workflow: ComplexModelEntity<'CompanyWorkflow'>) => void,
    showAddButton?: boolean,
    workflowIdFilter?: string,
    addButtonTitle?: string,
    ListItemCard?: (args: ListItemCardProps) => JSX.Element,
    editPanelVisible?: boolean,
}) {
    const [selectedIndex, setSelectedIndex] = useState<number>(0);
    const [addDialogOpen, setAddDialogOpen] = useState<boolean>(false);
    const [settingsDialogOpen, setSettingsDialogOpen] = useState<boolean>(false);
    const { error } = useToast();

    const { companyWorkflows, loaded } = useCompanyWorkflow(workflowIdFilter);
    const allWorkflows = useWorkflow() || [];
    const filterWorkflow = allWorkflows.find(item => item.workflowId === workflowIdFilter);
    const maxAllowedReached = filterWorkflow && filterWorkflow.max_instances_allowed === companyWorkflows.length;

    useEffect(() => {
        if (editPanelVisible) {
            setSelectedIndex(companyWorkflows.length ? 0 : 1);
        }
    }, [companyWorkflows.length]);

    const handleListItemClick = (
        event: React.MouseEvent,
        index: number,
    ) => {
        if (onItemSelected) {
            onItemSelected(companyWorkflows[index]);
        }
        setSelectedIndex(index);
    };

    const handleAddItemSelected = async (workflow: ComplexModelEntity<'Workflow'>) => {
        const dataWrapper: DataWrapper = getDataWrapper();
        const company = await dataWrapper.getComplexDataObject(complexFindModel('Company'), AuthManager.getCompany().get('id')!);
        const alreadyAdded = await company.getWorkflowsByWorkflowId(workflow.id);
        if (alreadyAdded.length === workflow.max_instances_allowed) {
            error('You have reached the maximum number of instances for this workflow');
            return null;
        }
        const workflowAdded = await company.addCompanyWorkflow({
            company_id: AuthManager.getCompany().get('id')!,
            workflow_id: workflow.id,
            settings: workflow.default_settings,
            trigger_on: workflow.default_settings?.triggerOn,
        });
        await dataWrapper.saveComplexDataObject(workflowAdded);
        return workflowAdded;
    };

    const handleSettingsButtonClick = (index: number) => {
        setSelectedIndex(index);
        setSettingsDialogOpen(true);
    };

    const handleAddItemClick = async () => {
        if (!workflowIdFilter) {
            setAddDialogOpen(true);
        } else {
            const workflow = allWorkflows.find(item => item.workflowId === workflowIdFilter);
            if (workflow) {
                const newWorkflow = await handleAddItemSelected(workflow);
                if (newWorkflow) {
                    handleSettingsButtonClick(companyWorkflows.length);
                }
            } else {
                console.log(`Workflow with id ${workflowIdFilter} not found`);
            }
        }
    };
    const handleDeleteButtonClick = async (index: number) => {
        const dataWrapper: DataWrapper = getDataWrapper();
        const company = await dataWrapper.getComplexDataObject(complexFindModel('Company'), AuthManager.getCompany().get('id')!);
        const complexCompanyWorkflow = await company.removeCompanyWorkflow(companyWorkflows[index]);
        await complexCompanyWorkflow.delete();
        await dataWrapper.saveComplexDataObject(complexCompanyWorkflow);
    };

    const ListItemButtonVariant = editPanelVisible ? ListItemButton : CompanyWorkflowListItemButton;

    return (
        <CompanyWorkflowListRoot>
            <OptionalCompanyWorkflowContainer
                renderIf={loaded}
                defaultElement={<Skeleton variant="rectangular" width={300} height={590} animation="wave" />}
                disablePadding
                subheader={null}
            >
                {companyWorkflows.map((workflow: CompanyWorkflowWithAssociatedWorkflow, index) => (
                    <CompanyWorkFlowListItem
                        disableGutters
                        key={String(workflow.id)}
                        endIcon={!editPanelVisible && (
                            <>
                                <Button
                                    buttonType="error"
                                    startIcon={<DeleteForeverIcon />}
                                    onClick={() => handleDeleteButtonClick(index)}
                                />
                                <Button
                                    buttonType="secondary"
                                    startIcon={<SettingsIcon />}
                                    onClick={() => handleSettingsButtonClick(index)}
                                />
                            </>
                        )}
                    >
                        <ListItemButtonVariant
                            selected={editPanelVisible && selectedIndex === index}
                            onClick={event => handleListItemClick(event, index)}
                        >
                            <ListItemCard workflow={workflow} index={index} />

                        </ListItemButtonVariant>
                    </CompanyWorkFlowListItem>
                ))}
                <OptionalListItem
                    renderIf={showAddButton && !maxAllowedReached}
                    key={-1}
                >
                    <Button
                        autoFocus
                        onClick={() => handleAddItemClick()}
                    >
                        {addButtonTitle}
                    </Button>
                </OptionalListItem>
            </OptionalCompanyWorkflowContainer>
            <SelectWorkflowPopup
                open={addDialogOpen}
                onClose={() => setAddDialogOpen(false)}
                onItemSelected={handleAddItemSelected}
            />
            <CompanyWorkflowListDialog
                open={settingsDialogOpen}
                onClose={() => setSettingsDialogOpen(false)}
                title="Reminder settings"
                buttons={[]}
            >
                <CompanyWorkflowSettings
                    companyWorkflow={companyWorkflows[selectedIndex]}
                    onSaveHandler={() => setSettingsDialogOpen(false)}
                    onDeleteHandler={() => setSettingsDialogOpen(false)}
                />
            </CompanyWorkflowListDialog>
            {(editPanelVisible ? <CompanyWorkflowSettings companyWorkflow={companyWorkflows[selectedIndex]} /> : null)}
        </CompanyWorkflowListRoot>
    );
}
